import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { InfoPageLayout } from "@/components/pages/InfoPageLayout";
import {
  policyPageSchema, breadcrumbSchema, ldScripts,
} from "@/lib/seo/schema";

const TITLE = "Kebijakan Privasi - Kalender Indonesia";
const DESC = "Kebijakan privasi Kalender Indonesia: data apa yang kami kumpulkan, cookie, analytics, iklan, dan hak pengguna.";

export const Route = createFileRoute("/kebijakan-privasi")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Kebijakan Privasi" },
      { property: "og:description", content: "Bagaimana Kalender Indonesia menangani data dan privasi Anda." },
    ],
    links: [{ rel: "canonical", href: "https://hari.co.id/kebijakan-privasi/" }],
    scripts: ldScripts([
      policyPageSchema({ url: "/kebijakan-privasi", name: TITLE, description: DESC, kind: "PrivacyPolicy", locale: "id", dateModified: "2026-05-01" }),
      breadcrumbSchema([
        { name: "Beranda", url: "/" },
        { name: "Kebijakan Privasi", url: "/kebijakan-privasi" },
      ]),
    ]),
  }),
  component: PrivacyPage,
});

const TOC = [
  { id: "data", label: "Data yang kami kumpulkan" },
  { id: "cookies", label: "Cookie & analytics" },
  { id: "ads", label: "Iklan" },
  { id: "storage", label: "Penyimpanan data" },
  { id: "rights", label: "Hak Anda" },
  { id: "changes", label: "Perubahan kebijakan" },
];

function PrivacyPage() {
  return (
    <SiteShell>
      <InfoPageLayout
        variant="legal"
        title="Kebijakan Privasi"
        subtitle="Transparansi soal data, cookie, dan iklan."
        badge="P"
        lastUpdated="1 Mei 2026"
        locale="id"
        breadcrumbs={[{ label: "Beranda", href: "/" }, { label: "Kebijakan Privasi" }]}
        aside={
          <div className="rounded-xl border border-border bg-card p-4 text-sm">
            <h3 className="mb-2 font-semibold">Daftar isi</h3>
            <ol className="space-y-1.5 text-muted-foreground">
              {TOC.map((t, i) => (
                <li key={t.id}>
                  <a className="hover:text-primary" href={`#${t.id}`}>{i + 1}. {t.label}</a>
                </li>
              ))}
            </ol>
          </div>
        }
      >
        <p>
          Kalender Indonesia ("kami") menghargai privasi pengguna. Halaman ini
          menjelaskan data apa yang kami kumpulkan ketika Anda menggunakan situs
          ini dan bagaimana data tersebut digunakan.
        </p>

        <h2 id="data">1. Data yang kami kumpulkan</h2>
        <ul>
          <li><strong>Data teknis</strong>: alamat IP, jenis browser, perangkat, halaman yang dikunjungi, waktu kunjungan.</li>
          <li><strong>Preferensi</strong>: tema (terang/gelap) dan pilihan bahasa disimpan di <code>localStorage</code> peramban Anda - tidak dikirim ke server.</li>
          <li>Kami <strong>tidak</strong> meminta nama, email, atau data identitas pribadi lain untuk menggunakan situs.</li>
        </ul>

        <h2 id="cookies">2. Cookie &amp; analytics</h2>
        <p>
          Kami dapat menggunakan layanan analitik pihak ketiga (misalnya Google
          Analytics atau Plausible) untuk memahami pola penggunaan secara
          agregat. Anda dapat memblokir cookie melalui pengaturan browser.
        </p>

        <h2 id="ads">3. Iklan</h2>
        <p>
          Beberapa halaman dapat menampilkan iklan dari mitra (mis. Google
          AdSense). Mitra iklan dapat menggunakan cookie untuk personalisasi.
          Silakan baca kebijakan privasi masing-masing mitra.
        </p>

        <h2 id="storage">4. Penyimpanan data</h2>
        <p>
          Data libur nasional, cuti bersama, dan konversi penanggalan bersifat
          publik dan tidak terkait dengan identitas pengguna manapun.
        </p>

        <h2 id="rights">5. Hak Anda</h2>
        <p>
          Anda berhak meminta penghapusan data analitik terkait kunjungan Anda
          dengan menghubungi kami melalui halaman <Link to="/kontak/">Kontak</Link>.
        </p>

        <h2 id="changes">6. Perubahan kebijakan</h2>
        <p>
          Kebijakan ini dapat diperbarui sewaktu-waktu. Tanggal pembaruan akan
          tercantum di bagian atas halaman.
        </p>
      </InfoPageLayout>
    </SiteShell>
  );
}